import { find, get } from 'lodash';
import { SubjectAreas } from 'Src/ng2/shared/constants/subject-areas.constant';
import { IFlag } from 'Src/ng2/shared/typings/interfaces/flags.interface';
import { IStudentSupport } from '../../shared/typings/interfaces/student-support.interface';
import { ISubjectArea } from './../../../ng2/shared/constants/subject-areas.constant';
import { COLUMN_DATA_TYPE } from './../../shared/constants/list-view/cell-type.constant';

export interface IManagerViewColumn {
  human: string;
  key: string;
  cellType: string;
  cellConfig: any;
  dataType: string;
  orderBy: string;
  getValueAtPath: (...args: any[]) => {};
}

// for flag based graphs
export const VALID_FLAG_TYPES = {
  POST_SECONDARY_FLAGS: null,
  POST_SECONDARY_PLANS: null,
  NEEDS_ATT_SUPPORT: 'Needs attendance support',
  ABSENT_TODAY: 'Absent today',
  NEEDS_MATH_SUPPORT: 'Needs Math support',
  NEEDS_ELA_SUPPORT: 'Needs ELA support',
  FAILURES_WITH_NO_SUPPORT: 'Failure with no support',
};

export const students = {
  human: 'Students',
  key: 'students',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'asc',
  getValueAtPath: (students: any[]) => {
    const data = students.length;
    const dependencies = { filterValue: data };
    return { data, dependencies };
  },
};

export const GRAPH_COLUMNS = {
  POST_SECONDARY_FLAGS: {
    10: students,
    20: {
      human: 'Unresolved',
      key: 'unresolved',
      cellType: COLUMN_DATA_TYPE.GENERIC,
      cellConfig: {},
      dataType: 'Number',
      orderBy: 'asc',
      getValueAtPath: (students: Array<{ flags: IFlag[] }>) =>
        getUnResolvedFlagsForFlagType(students, VALID_FLAG_TYPES.POST_SECONDARY_FLAGS),
    },
    30: {
      human: 'Ignored',
      key: 'ignored',
      cellType: COLUMN_DATA_TYPE.GENERIC,
      cellConfig: {},
      dataType: 'Number',
      orderBy: 'asc',
      getValueAtPath: (students: Array<{ flags: IFlag[] }>) =>
        getIgnoredFlagsForFlagType(students, VALID_FLAG_TYPES.POST_SECONDARY_FLAGS),
    },
    40: {
      human: 'Resolved',
      key: 'resolved',
      cellType: COLUMN_DATA_TYPE.GENERIC,
      cellConfig: {},
      dataType: 'Number',
      orderBy: 'asc',
      getValueAtPath: (students: Array<{ flags: IFlag[] }>) =>
        getResolvedFlagsForFlagType(students, VALID_FLAG_TYPES.POST_SECONDARY_FLAGS),
    },
  },

  POST_SECONDARY_PLANS: {
    10: students,
    20: {
      human: 'Unresolved',
      key: 'unresolved',
      cellType: COLUMN_DATA_TYPE.GENERIC,
      cellConfig: {},
      dataType: 'Number',
      orderBy: 'asc',
      getValueAtPath: (students: Array<{ flags: IFlag[] }>) =>
        getUnResolvedFlagsForFlagType(students, VALID_FLAG_TYPES.POST_SECONDARY_PLANS),
    },
    30: {
      human: 'Ignored',
      key: 'ignored',
      cellType: COLUMN_DATA_TYPE.GENERIC,
      cellConfig: {},
      dataType: 'Number',
      orderBy: 'asc',
      getValueAtPath: (students: Array<{ flags: IFlag[] }>) =>
        getIgnoredFlagsForFlagType(students, VALID_FLAG_TYPES.POST_SECONDARY_PLANS),
    },
    40: {
      human: 'Resolved',
      key: 'resolved',
      cellType: COLUMN_DATA_TYPE.GENERIC,
      cellConfig: {},
      dataType: 'Number',
      orderBy: 'asc',
      getValueAtPath: (students: Array<{ flags: IFlag[] }>) =>
        getResolvedFlagsForFlagType(students, VALID_FLAG_TYPES.POST_SECONDARY_PLANS),
    },
  },

  FAILURES_WITH_NO_SUPPORT: {
    10: {
      human: '% Failing',
      key: 'percentageFailing',
      cellType: COLUMN_DATA_TYPE.GENERIC,
      cellConfig: {},
      dataType: 'Number',
      orderBy: 'asc',
      getValueAtPath: (
        students: Array<{ currProgram: { grades: any[] } }>,
        config: { grouping: string; subject?: string; course?: string; teacher?: string },
      ) => {
        const { grouping, subject, course, teacher } = config;
        if (grouping === 'SUBJECT') return getPercentageFailingForSubject(students, subject);
        if (grouping === 'COURSE') return getPercentageFailingForCourse(students, course);
        if (grouping === 'TEACHER') return getPercentageFailingForTeacher(students, teacher);
        return { data: '?', dependencies: {} };
      },
    },
    20: {
      human: '% Failing High Priority',
      key: 'percentageFailingHp',
      cellType: COLUMN_DATA_TYPE.GENERIC,
      cellConfig: {},
      dataType: 'Number',
      orderBy: 'asc',
      getValueAtPath: (
        students: Array<{ currProgram: { grades: any[] } }>,
        config: { grouping: string; subject?: string; course?: string; teacher?: string },
      ): { data: number | string; dependencies: {} } => {
        const { grouping, subject, course, teacher } = config;
        if (grouping === 'SUBJECT') return getPercentageHpFailingForSubject(students, subject);
        if (grouping === 'COURSE') return getPercentageHpFailingForCourse(students, course);
        if (grouping === 'TEACHER') return getPercentageHpFailingForTeacher(students, teacher);
      },
    },
    30: {
      human: 'High Priority Failures With No Support',
      key: 'hpFailuresWithNoSupport',
      cellType: COLUMN_DATA_TYPE.GENERIC,
      cellConfig: {},
      dataType: 'Number',
      orderBy: 'asc',
      getValueAtPath: (
        students: Array<{ currProgram: { grades: any[] }; activeStudentSupports: IStudentSupport[] }>,
        config: { grouping: string; subject?: string; course?: string; teacher?: string },
      ): { data: number; dependencies: {} } => {
        if (config.grouping === 'TEACHER') return getCountOfHpFailuresNeedingSupport(students, config.teacher);
        if (config.grouping === 'COURSE') return getCountOfHpFailuresNeedingSupportForCourse(students, config.course);
        if (config.grouping === 'SUBJECT') return getCountOfHpFailuresNeedingSupportForSubj(students, config.subject);
      },
    },
  },
};

// UTIL FUNCTIONS
function getResolvedFlagsForFlagType(students: Array<{ flags: IFlag[] }>, flagType: string) {
  const resolvedFlags = students.reduce((studentFlags, student) => {
    const { flags } = student;
    flags.forEach(flag => {
      const isResolved = flag.status === 'RESOLVED';
      const isFlagForGraph = flag.type === flagType;
      if (isResolved && isFlagForGraph) studentFlags.push(flag);
    });
    return studentFlags;
  }, []);

  const resolvedFlagsCount = resolvedFlags.length;
  const data = resolvedFlagsCount;
  const dependencies = { filterValue: data };
  return { data, dependencies };
}

function getUnResolvedFlagsForFlagType(students: Array<{ flags: IFlag[] }>, flagType: string) {
  const unresolvedFlags = students.reduce((studentFlags, student) => {
    const { flags } = student;
    flags.forEach(flag => {
      const { status, type, isIgnored } = flag;
      const isActive = status === 'ACTIVE';
      const isFlagForGraph = type === flagType;
      if (isActive && isFlagForGraph && !isIgnored) studentFlags.push(flag);
    });
    return studentFlags;
  }, []);

  const resolvedFlagsCount = unresolvedFlags.length;
  const data = resolvedFlagsCount;
  const dependencies = { filterValue: data };
  return { data, dependencies };
}

function getIgnoredFlagsForFlagType(students: Array<{ flags: IFlag[] }>, flagType: string) {
  const ignoredFlags = students.reduce((studentFlags, student) => {
    const { flags } = student;
    flags.forEach(flag => {
      const isIgnored = flag.isIgnored;
      const isFlagForGraph = flag.type === flagType;
      if (isIgnored && isFlagForGraph) studentFlags.push(flag);
    });
    return studentFlags;
  }, []);

  const ignoredFlagsCount = ignoredFlags.length;
  const data = ignoredFlagsCount;
  const dependencies = { filterValue: data };
  return { data, dependencies };
}

function getPercentageFailingForSubject(students: Array<{ currProgram: { grades: any[] } }>, subject: string) {
  const { failing, totalWithGradeForSubject } = students.reduce(
    (subjectData, student) => {
      const {
        currProgram: { grades },
      } = student;
      grades.forEach(({ subj, pf, mostRecent }) => {
        if (subj === subject && mostRecent) {
          subjectData.totalWithGradeForSubject++;
          if (pf === 'P') subjectData.passing++;
          if (pf === 'F') subjectData.failing++;
        }
      });
      return subjectData;
    },
    { totalWithGradeForSubject: 0, passing: 0, failing: 0 },
  );

  const percentageFailingSubject = (failing / totalWithGradeForSubject) * 100;
  const data = totalWithGradeForSubject ? Math.round(percentageFailingSubject) : 0;
  const dependencies = { filterValue: data };
  return { data, dependencies };
}

function getPercentageHpFailingForSubject(
  students: Array<{ currProgram: { grades: any[] } }>,
  subject: string,
): { data: number; dependencies: {} } {
  const { failing, totalWithGradeForSubject } = students.reduce(
    (subjectData, student) => {
      const {
        currProgram: { grades },
      } = student;
      grades.forEach(({ subj, pf, mostRecent, priority }) => {
        const gradeAlignsToSubject = subj === subject;
        const isMostRecentGrade = mostRecent;
        const isHighPriority = priority === 'high';
        if (gradeAlignsToSubject && isMostRecentGrade && isHighPriority) {
          subjectData.totalWithGradeForSubject++;
          if (pf === 'P') subjectData.passing++;
          if (pf === 'F') subjectData.failing++;
        }
      });
      return subjectData;
    },
    { totalWithGradeForSubject: 0, passing: 0, failing: 0 },
  );

  const percentageFailingSubject = (failing / totalWithGradeForSubject) * 100;
  const data = totalWithGradeForSubject ? Math.round(percentageFailingSubject) : 0;
  const dependencies = {};

  return { data, dependencies };
}

function getPercentageFailingForCourse(students, courseCode) {
  const { failing, totalWithCourse } = students.reduce(
    (courseData, student) => {
      const {
        currProgram: { grades },
      } = student;
      grades.forEach(({ pf, mostRecent, code }) => {
        if (code === courseCode && mostRecent) {
          courseData.totalWithCourse++;
          if (pf === 'P') courseData.passing++;
          if (pf === 'F') courseData.failing++;
        }
      });
      return courseData;
    },
    { totalWithCourse: 0, passing: 0, failing: 0 },
  );

  const percentageFailingCourse = (failing / totalWithCourse) * 100;
  const data = totalWithCourse ? Math.round(percentageFailingCourse) : 0;
  const dependencies = { filterValue: data };
  return { data, dependencies };
}

function getPercentageFailingForTeacher(students, teacher) {
  const { failing, totalWithTeacher } = students.reduce(
    (teacherData, student) => {
      const {
        currProgram: { grades },
      } = student;
      grades.forEach(({ pf, mostRecent, teacher: { nickName } }) => {
        if (nickName === teacher && mostRecent) {
          teacherData.totalWithTeacher++;
          if (pf === 'F') teacherData.failing++;
        }
      });
      return teacherData;
    },
    { totalWithTeacher: 0, failing: 0 },
  );

  const percentageFailingCourse = (failing / totalWithTeacher) * 100;
  const data = totalWithTeacher ? Math.round(percentageFailingCourse) : 0;
  const dependencies = {};
  return { data, dependencies };
}

function getPercentageHpFailingForTeacher(students, teacher) {
  const { failing, totalWithTeacher } = students.reduce(
    (teacherData, student) => {
      const {
        currProgram: { grades },
      } = student;
      grades.forEach(({ pf, mostRecent, teacher: { nickName }, priority }) => {
        const isHighPriority = priority === 'high';
        if (nickName === teacher && mostRecent && isHighPriority) {
          teacherData.totalWithTeacher++;
          if (pf === 'F') teacherData.failing++;
        }
      });
      return teacherData;
    },
    { totalWithTeacher: 0, failing: 0 },
  );

  const percentageFailingCourse = (failing / totalWithTeacher) * 100;
  const data = totalWithTeacher ? Math.round(percentageFailingCourse) : 0;
  const dependencies = {};
  return { data, dependencies };
}

function getPercentageHpFailingForCourse(
  students: Array<{ currProgram: { grades: any[] } }>,
  courseCode: string,
): { data: number; dependencies: {} } {
  const { failing, totalWithCourse } = students.reduce(
    (courseData, student) => {
      const {
        currProgram: { grades },
      } = student;

      grades.forEach(({ pf, mostRecent, code, priority }) => {
        const isFailing = pf === 'F';
        const isPassing = pf === 'P';
        const gradeAlignsToCode = code === courseCode;
        const isMostRecentGrade = mostRecent;
        const isHighPriority = priority === 'high';

        if (gradeAlignsToCode && isMostRecentGrade && isHighPriority) {
          courseData.totalWithCourse++;
          if (isPassing) courseData.passing++;
          if (isFailing) courseData.failing++;
        }
      });
      return courseData;
    },
    { totalWithCourse: 0, passing: 0, failing: 0 },
  );

  const percentageFailingCourse = (failing / totalWithCourse) * 100;
  const data = totalWithCourse ? Math.round(percentageFailingCourse) : 0;
  const dependencies = {};
  return { data, dependencies };
}

function getCountOfFailuresNeedingSupportForSubj(
  students: Array<{ activeStudentSupports: IStudentSupport[]; currProgram: { grades: any[] } }>,
  subject: string,
) {
  const data = students.reduce((totalUnsupportFailureCount, student) => {
    const { activeStudentSupports } = student;
    const academicStudentSupports = activeStudentSupports.filter(
      // academic supports schema allows null
      ({ support }) => support.metaData && support.category === 'ACADEMIC',
    );
    const {
      currProgram: { grades },
    } = student;
    const subjectsWithSupport = academicStudentSupports.reduce(
      (
        subjectsWithSupport,
        {
          support: {
            metaData: { subject: supportSubj },
          },
        },
      ) => {
        const subjectAreaData: ISubjectArea = find(SubjectAreas, { humanShort: supportSubj });
        if (!subjectAreaData) return subjectsWithSupport;
        const { key: subjectKey } = subjectAreaData;
        subjectsWithSupport[subjectKey] = true;
        return subjectsWithSupport;
      },
      {},
    );

    const isFailingAndUnsupported = course => {
      const { subj, pf, mostRecent } = course;
      return subj === subject && mostRecent && pf === 'F' && !subjectsWithSupport[subj];
    };

    const failedSubjectsCount = grades.reduce((failedWithoutSupportCount, course) => {
      if (isFailingAndUnsupported(course)) failedWithoutSupportCount++;
      return failedWithoutSupportCount;
    }, 0);

    return (totalUnsupportFailureCount += failedSubjectsCount);
  }, 0);
  const dependencies = { filterValue: data };

  return { data, dependencies };
}

function getCountOfHpFailuresNeedingSupport(
  students: Array<{ activeStudentSupports: IStudentSupport[]; currProgram: { grades: any[] } }>,
  teacher: string,
) {
  const dependencies = {};

  const data = students.reduce((totalUnsupportFailureCount, student) => {
    const {
      activeStudentSupports,
      currProgram: { grades },
    } = student;

    const subjectsWithSupport = activeStudentSupports.reduce(
      (subjectsWithSupport, { support: { category, metaData } }) => {
        // only focused on ACADEMIC studentSupports with metaData
        if (!metaData || category !== 'ACADEMIC') return subjectsWithSupport;
        const subjectAreaData: ISubjectArea = find(SubjectAreas, { humanShort: metaData.subject });
        if (!subjectAreaData) return subjectsWithSupport;
        const { key: subjectKey } = subjectAreaData;
        subjectsWithSupport[subjectKey] = true;
        return subjectsWithSupport;
      },
      {},
    );

    const isFailingAndUnsupported = course => {
      const {
        subj,
        pf,
        mostRecent,
        priority,
        teacher: { nickName },
      } = course;
      return teacher === nickName && mostRecent && pf === 'F' && !subjectsWithSupport[subj] && priority === 'high';
    };

    const failedSubjectsCount = grades.reduce((failedWithoutSupportCount, course) => {
      if (isFailingAndUnsupported(course)) failedWithoutSupportCount++;
      return failedWithoutSupportCount;
    }, 0);

    return (totalUnsupportFailureCount += failedSubjectsCount);
  }, 0);

  return { data, dependencies };
}

function getCountOfHpFailuresNeedingSupportForSubj(
  students: Array<{ activeStudentSupports: IStudentSupport[]; currProgram: { grades: any[] } }>,
  subject: string,
) {
  const dependencies = {};

  const data = students.reduce((totalUnsupportFailureCount, student) => {
    const {
      activeStudentSupports,
      currProgram: { grades },
    } = student;

    const subjectsWithSupport = activeStudentSupports.reduce(
      (subjectsWithSupport, { support: { category, metaData } }) => {
        // only focused on ACADEMIC studentSupports with metaData
        if (!metaData || category !== 'ACADEMIC') return subjectsWithSupport;

        const subjectAreaData: ISubjectArea = find(SubjectAreas, { humanShort: metaData.subject });

        if (!subjectAreaData) return subjectsWithSupport;

        const { key: subjectKey } = subjectAreaData;
        subjectsWithSupport[subjectKey] = true;
        return subjectsWithSupport;
      },
      {},
    );

    const isFailingAndUnsupported = _course => {
      const { subj, pf, mostRecent, priority } = _course;
      const subjectAligns = subj === subject;
      const isMostRecent = mostRecent;
      const hasSupport = subjectsWithSupport[subj];
      const isHighPriority = priority === 'high';
      const isFailing = pf === 'F';
      return subjectAligns && isMostRecent && !hasSupport && isHighPriority && isFailing;
    };

    const failedSubjectsCount = grades.reduce((failedWithoutSupportCount, course) => {
      if (isFailingAndUnsupported(course)) failedWithoutSupportCount++;
      return failedWithoutSupportCount;
    }, 0);

    return (totalUnsupportFailureCount += failedSubjectsCount);
  }, 0);

  return { data, dependencies };
}

function getCountOfHpFailuresNeedingSupportForCourse(
  students: Array<{ activeStudentSupports: IStudentSupport[]; currProgram: { grades: any[] } }>,
  course,
) {
  const dependencies = {};

  const data = students.reduce((totalUnsupportFailureCount, student) => {
    const {
      activeStudentSupports,
      currProgram: { grades },
    } = student;

    const subjectsWithSupport = activeStudentSupports.reduce(
      (subjectsWithSupport, { support: { category, metaData } }) => {
        // only focused on ACADEMIC studentSupports with metaData
        if (!metaData || category !== 'ACADEMIC') return subjectsWithSupport;

        const subjectAreaData: ISubjectArea = find(SubjectAreas, { humanShort: metaData.subject });

        if (!subjectAreaData) return subjectsWithSupport;

        const { key: subjectKey } = subjectAreaData;
        subjectsWithSupport[subjectKey] = true;
        return subjectsWithSupport;
      },
      {},
    );

    const isFailingAndUnsupported = _course => {
      const { subj, pf, mostRecent, priority, code } = _course;
      const courseAligns = course === code;
      const isMostRecent = mostRecent;
      const hasSupport = subjectsWithSupport[subj];
      const isHighPriority = priority === 'high';
      const isFailing = pf === 'F';
      return courseAligns && isMostRecent && !hasSupport && isHighPriority && isFailing;
    };

    const failedSubjectsCount = grades.reduce((failedWithoutSupportCount, course) => {
      if (isFailingAndUnsupported(course)) failedWithoutSupportCount++;
      return failedWithoutSupportCount;
    }, 0);

    return (totalUnsupportFailureCount += failedSubjectsCount);
  }, 0);

  return { data, dependencies };
}

function getCountOfFailuresWithSupportForSubj(
  students: Array<{ activeStudentSupports: IStudentSupport[]; currProgram: { grades: any[] } }>,
  subject: string,
) {
  const data = students.reduce((totalSupportedFailureCount, student) => {
    const { activeStudentSupports } = student;
    const academicSupports = activeStudentSupports.filter(
      // academic support allow null
      ({ support }) => support.metaData && support.category === 'ACADEMIC',
    );

    const {
      currProgram: { grades },
    } = student;

    const subjectsWithSupport = academicSupports.reduce(
      (
        subjectsWithSupport,
        {
          support: {
            metaData: { subject },
          },
        },
      ) => {
        // TODO: Work with data team to eliminate the need for this mapping(JJ)
        const subjectAreaData: ISubjectArea = find(SubjectAreas, { humanShort: subject });

        if (!subjectAreaData) return subjectsWithSupport;

        const { key: subjectKey } = subjectAreaData;
        subjectsWithSupport[subjectKey] = true;
        return subjectsWithSupport;
      },
      {},
    );

    const isFailingAndSupported = course => {
      const { subj, pf, mostRecent } = course;
      return subj === subject && mostRecent && pf === 'F' && subjectsWithSupport[subj];
    };

    const failingWithSupportCount = grades.reduce((failedWithSupportCount, course) => {
      if (isFailingAndSupported(course)) failedWithSupportCount++;
      return failedWithSupportCount;
    }, 0);

    return (totalSupportedFailureCount += failingWithSupportCount);
  }, 0);

  const dependencies = { filterValue: data };

  return { data, dependencies };
}

export const utilityFunctions = {
  getCountOfFailuresWithSupportForSubj,
  getCountOfHpFailuresNeedingSupportForSubj,
  getCountOfHpFailuresNeedingSupportForCourse,
  getCountOfHpFailuresNeedingSupport,
  getCountOfFailuresNeedingSupportForSubj,
  getPercentageHpFailingForCourse,
  getPercentageHpFailingForTeacher,
  getPercentageFailingForTeacher,
  getPercentageFailingForCourse,
  getPercentageHpFailingForSubject,
  getPercentageFailingForSubject,
  getIgnoredFlagsForFlagType,
  getUnResolvedFlagsForFlagType,
  getResolvedFlagsForFlagType,
};
